import { useState, useEffect, useCallback, useRef } from 'react';
import * as Tone from 'tone';
import { Piano } from '@tonejs/piano';
import { createPianoLoadStateMachine, type PianoLoadState } from './pianoLoadState';
import { PIANO_LOAD_TIMEOUT_MS } from '@/utils/loadTimeouts';
import { recordSeamAttack, recordSeamCancel } from '@/utils/testSeam';
import { getAudioNow } from '@/utils/audioNow';

const LOAD_TIMEOUT_MS = PIANO_LOAD_TIMEOUT_MS;

/**
 * OTO-29 T3: installed `@tonejs/piano@0.2.1` constraint (`Piano.ts` 289-307,
 * `Strings.ts` 70-75) -- `keyUp` synchronously deletes `_heldNotes` and
 * delegates to `Strings.triggerRelease`, which synchronously deletes
 * `_activeNotes`, REGARDLESS of whether the `time` passed to `keyUp` is in
 * the future. Consequently a pitch can never be "held" ahead of its actual
 * sounding moment: calling `keyDown({ time: future })` eagerly would mark it
 * held immediately, and a same-pitch retrigger scheduled before that future
 * time would silently no-op (`keyDown` ignores an already-held midi).
 *
 * This hook therefore owns its own cancellable scheduling registry, built on
 * `Tone.context.setTimeout`/`clearTimeout` (audio-clock-anchored, distinct
 * from JS `setTimeout`), and defers every `keyDown`/`keyUp` call to a
 * dispatch/cutoff/cleanup timer fired close to the moment it should
 * audibly happen -- never eagerly at `playNote()` call time.
 */
interface ScheduleEntry {
  id: number;
  pitch: number;
  velocity: number;
  attackTime: number;
  cutoffTime?: number;
  dispatched: boolean;
  dispatchTimeoutId?: number;
  cutoffTimeoutId?: number;
  cleanupTimeoutId?: number;
}

/** Sentinel owner for a pitch played via the immediate/interactive path
 *  (no `ScheduleEntry` -- `keyDown` already ran synchronously). */
const IMMEDIATE_OWNER = Symbol('immediate-owner');
type Owner = number | typeof IMMEDIATE_OWNER;

interface UsePianoAudioReturn {
  /** Play a note. With `time` in the future, the attack is scheduled and
   *  individually cancellable via `cancelScheduled()`; with no `time` (or a
   *  past/current one) it plays immediately. */
  playNote: (pitch: number, velocity?: number, time?: number, cutoffAfterS?: number) => void;
  /** Stop a note, optionally at an explicit audio time */
  stopNote: (pitch: number, time?: number) => void;
  /** Stop all notes: cancels pending scheduled attacks, then releases everything sounding */
  stopAllNotes: () => void;
  /** Cancels every strictly-future scheduled attack WITHOUT releasing sounding
   *  notes (tempo-only reconciliation, OTO-22 §2b) */
  cancelScheduled: () => void;
  /** Whether the piano samples are loaded */
  isLoaded: boolean;
  /** Loading/error state, incl. timeout */
  loadState: PianoLoadState;
  /** Re-creates the piano after a load error/timeout */
  retry: () => void;
  /** Loading progress (0-1) */
  loadingProgress: number;
  /** Start the audio context (must be called from user gesture) */
  startAudio: () => Promise<void>;
  /** Whether audio context is running */
  isAudioStarted: boolean;
}

/**
 * Hook for playing piano sounds using `@tonejs/piano`'s `Piano` instrument
 * (five-layer Salamander Grand Piano mapping), gain-staged through a fixed
 * `Tone.Volume(-12) -> Tone.Limiter(-1)` chain for consistent headroom.
 */
export function usePianoAudio(): UsePianoAudioReturn {
  const [loadState, setLoadState] = useState<PianoLoadState>('loading');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isAudioStarted, setIsAudioStarted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const pianoRef = useRef<Piano | null>(null);
  const volumeRef = useRef<Tone.Volume | null>(null);
  const limiterRef = useRef<Tone.Limiter | null>(null);
  const entriesRef = useRef<Map<number, ScheduleEntry>>(new Map());
  const ownerRef = useRef<Map<number, Owner>>(new Map());
  const nextIdRef = useRef(0);
  const isLoaded = loadState === 'loaded';

  const retry = useCallback(() => {
    setLoadState('loading');
    setLoadingProgress(0);
    setRetryCount((c) => c + 1);
  }, []);

  // Schedules `fn` to run near `target` (an absolute `getAudioNow()`-anchored
  // time), using the same audio-clock authority as `playNote`'s branch
  // decision. Always subtracts `Tone.context.lookAhead` so the underlying
  // Web Audio scheduling keeps its full look-ahead margin for `target`
  // (matching the installed Context.now/setTimeout relationship, without
  // depending on `Context.now()` itself) -- a single clamped-at-zero affine
  // formula for every target, so target-to-delay is strictly monotonic.
  // Targets already inside the look-ahead window all clamp to a delay of
  // zero and therefore tie, so Tone fires them in timer registration order
  // rather than attack-time order -- `dispatchEntry` restores attack-time
  // order for tied same-pitch dispatches.
  const scheduleAt = useCallback((target: number, fn: () => void): number => {
    const delay = Math.max(0, target - getAudioNow() - Tone.context.lookAhead);
    return Tone.context.setTimeout(fn, delay);
  }, []);

  const clearEntryTimers = useCallback((entry: ScheduleEntry) => {
    if (entry.dispatchTimeoutId !== undefined) {
      Tone.context.clearTimeout(entry.dispatchTimeoutId);
      entry.dispatchTimeoutId = undefined;
    }
    if (entry.cutoffTimeoutId !== undefined) {
      Tone.context.clearTimeout(entry.cutoffTimeoutId);
      entry.cutoffTimeoutId = undefined;
    }
    if (entry.cleanupTimeoutId !== undefined) {
      Tone.context.clearTimeout(entry.cleanupTimeoutId);
      entry.cleanupTimeoutId = undefined;
    }
  }, []);

  // Retires whatever currently owns `pitch` (an entry, dispatched and
  // holding a real note, or the immediate-path sentinel): clears the old
  // entry's cutoff/cleanup timers, removes it from the registry, then issues
  // the mandatory keyUp-before-keyDown release at `atTime`. A no-op when
  // nothing owns the pitch. By construction, `ownerRef` only ever maps a
  // pitch to an entry AFTER that entry has dispatched (i.e. actually holds
  // the note), so an owner always implies a real held note to release.
  const retireOwner = useCallback(
    (pitch: number, atTime: number) => {
      const instrument = pianoRef.current;
      if (!instrument) return;
      const ownerId = ownerRef.current.get(pitch);
      if (ownerId === undefined) return;

      if (ownerId !== IMMEDIATE_OWNER) {
        const oldEntry = entriesRef.current.get(ownerId);
        if (oldEntry) {
          clearEntryTimers(oldEntry);
          entriesRef.current.delete(ownerId);
        }
      }
      ownerRef.current.delete(pitch);
      instrument.keyUp({ midi: pitch, time: atTime });
    },
    [clearEntryTimers]
  );

  // A dispatch timer fires close to its entry's `attackTime`, but every
  // target inside `Tone.context.lookAhead` clamps to the same zero delay in
  // `scheduleAt`, so same-pitch entries registered out of attack-time order
  // can otherwise dispatch in registration order and invert the mandatory
  // keyUp-before-keyDown owner retirement. The first tied timer to fire
  // therefore drains every still-registered, undispatched same-pitch entry
  // up to and including itself, sorted by attackTime then id, running the
  // normal dispatch effects for each in that order; later queued callbacks
  // for entries pulled into an earlier batch find their entry already
  // dispatched (or gone) and no-op.
  const dispatchEntry = useCallback(
    (entry: ScheduleEntry) => {
      // OTO-31 follow-up: this runs INSIDE `entry`'s own Tone.context timer
      // callback. Tone's `Context._timeoutLoop` is `callback(); shift();` --
      // if the callback removes its own event via `clearTimeout`, `shift()`
      // then discards whatever event is next in the timeline WITHOUT running
      // it (observed: the treble dispatch timer of a simultaneous chord,
      // silently dropping the note). Clearing the id here, without touching
      // Tone's timeline, keeps the batch drain below (and every later
      // `clearEntryTimers`) from ever cancelling the currently-firing event.
      entry.dispatchTimeoutId = undefined;
      if (!entriesRef.current.has(entry.id) || entry.dispatched) return;
      const instrument = pianoRef.current;
      if (!instrument) return;

      const batch = [...entriesRef.current.values()]
        .filter(
          (candidate) =>
            !candidate.dispatched &&
            candidate.pitch === entry.pitch &&
            candidate.attackTime <= entry.attackTime
        )
        .sort((a, b) => a.attackTime - b.attackTime || a.id - b.id);

      for (const member of batch) {
        if (member.dispatchTimeoutId !== undefined) {
          Tone.context.clearTimeout(member.dispatchTimeoutId);
          member.dispatchTimeoutId = undefined;
        }
        // Retiring the previous owner (below) can delete a batch member from
        // `entriesRef` before its own turn -- re-check membership here.
        if (!entriesRef.current.has(member.id) || member.dispatched) continue;

        member.dispatched = true;
        const ownerId = ownerRef.current.get(member.pitch);
        if (ownerId !== undefined && ownerId !== member.id) {
          retireOwner(member.pitch, member.attackTime);
        } else if (ownerId === undefined) {
          // No owner for this pitch -- force-release any stale held state before the new attack
          instrument.keyUp({ midi: member.pitch, time: member.attackTime });
        }
        instrument.keyDown({
          midi: member.pitch,
          time: member.attackTime,
          velocity: member.velocity,
        });
        ownerRef.current.set(member.pitch, member.id);
      }
    },
    [retireOwner]
  );

  const cutoffEntry = useCallback((entry: ScheduleEntry) => {
    entry.cutoffTimeoutId = undefined;
    const instrument = pianoRef.current;
    if (!instrument) return;
    if (ownerRef.current.get(entry.pitch) === entry.id && entry.cutoffTime !== undefined) {
      instrument.keyUp({ midi: entry.pitch, time: entry.cutoffTime });
      ownerRef.current.delete(entry.pitch);
    }
  }, []);

  const cleanupEntry = useCallback((entry: ScheduleEntry) => {
    entry.cleanupTimeoutId = undefined;
    entriesRef.current.delete(entry.id);
  }, []);

  // Initialize the piano. Re-creates it on retryCount change (Retry button).
  useEffect(() => {
    let cancelled = false;
    const entries = entriesRef.current;
    const owners = ownerRef.current;

    const stateMachine = createPianoLoadStateMachine({
      timeoutMs: LOAD_TIMEOUT_MS,
      onStateChange: (state) => {
        if (cancelled) return;
        setLoadState(state);
        if (state === 'error') {
          console.error(
            '[usePianoAudio/init] Failed: sample load timed out | ' +
              `reason=no load()/error event within ${LOAD_TIMEOUT_MS}ms`
          );
        }
      },
    });

    // OTO-29: five-layer Salamander Grand Piano mapping via `@tonejs/piano`,
    // gain-staged Volume(-12) -> Limiter(-1) -> destination for consistent
    // headroom (replaces the raw Tone.Sampler + toDestination() chain).
    const piano = new Piano({
      velocities: 5,
      minNote: 21,
      maxNote: 108,
      maxPolyphony: 88,
      release: false,
      pedal: true,
      url: '/samples/salamander-v3/',
    });
    const volume = new Tone.Volume(-12);
    const limiter = new Tone.Limiter(-1);
    piano.connect(volume);
    volume.connect(limiter);
    limiter.toDestination();

    pianoRef.current = piano;
    volumeRef.current = volume;
    limiterRef.current = limiter;

    piano
      .load()
      .then(() => {
        if (cancelled) return;
        console.info('[usePianoAudio/init] Done: piano samples loaded');
        setLoadingProgress(1);
        stateMachine.markLoaded();
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[usePianoAudio/init] Failed: sample load error | reason=', err);
        stateMachine.markError();
      });

    return () => {
      cancelled = true;
      stateMachine.dispose();

      // Clear every dispatch/cutoff/cleanup timer still pending before
      // disposing, then release and dispose the whole gain chain.
      for (const entry of entries.values()) {
        clearEntryTimers(entry);
      }
      entries.clear();
      owners.clear();

      if (pianoRef.current) {
        pianoRef.current.stopAll();
        pianoRef.current.dispose();
        pianoRef.current = null;
      }
      if (volumeRef.current) {
        volumeRef.current.dispose();
        volumeRef.current = null;
      }
      if (limiterRef.current) {
        limiterRef.current.dispose();
        limiterRef.current = null;
      }
    };
  }, [retryCount, clearEntryTimers]);

  // Start audio context (must be called from user gesture)
  const startAudio = useCallback(async () => {
    if (Tone.context.state !== 'running') {
      await Tone.start();
    }
    setIsAudioStarted(true);
  }, []);

  // Play a note. `time` undefined (or <= now) plays immediately; `time` in
  // the future registers a cancellable dispatch/cutoff/cleanup schedule
  // (see the module doc comment for why the actual keyDown/keyUp calls must
  // be deferred rather than issued eagerly with a future `time`).
  const playNote = useCallback(
    (pitch: number, velocity: number = 0.8, time?: number, cutoffAfterS?: number) => {
      const instrument = pianoRef.current;
      if (!instrument || !isLoaded) return;

      // Start audio context if not started
      if (Tone.context.state !== 'running') {
        Tone.start().catch((err) => {
          console.error('[usePianoAudio/playNote] Failed: audio context start | reason=', err);
        });
        setIsAudioStarted(true);
      }

      if (time !== undefined && !Number.isFinite(time)) {
        console.error(
          `[usePianoAudio/playNote] Failed: non-finite scheduled time=${time} for pitch=${pitch}`
        );
        throw new Error(
          `[usePianoAudio/playNote] Failed: non-finite scheduled time=${time} for pitch=${pitch}`
        );
      }

      // Zero/negative velocity: silent no-op, nothing to schedule or play.
      if (velocity <= 0) return;

      const audioNow = getAudioNow();

      if (time === undefined || time <= audioNow) {
        // Immediate/interactive path: retire whatever currently owns this
        // pitch (mandatory keyUp-before-keyDown), then keyDown right away.
        const effectiveTime = time ?? audioNow;
        retireOwner(pitch, effectiveTime);
        instrument.keyDown({ midi: pitch, time: effectiveTime, velocity });
        ownerRef.current.set(pitch, IMMEDIATE_OWNER);
        return;
      }

      // Future-scheduled path.
      recordSeamAttack(pitch, time);
      const id = nextIdRef.current++;
      const cutoffTime = cutoffAfterS !== undefined ? time + cutoffAfterS : undefined;
      const entry: ScheduleEntry = {
        id,
        pitch,
        velocity,
        attackTime: time,
        cutoffTime,
        dispatched: false,
      };
      entriesRef.current.set(id, entry);

      // Dispatch is anchored to `attackTime` via `scheduleAt` (fires in
      // Tone's look-ahead window around it), not deferred to the next
      // Tone.context tick. Each entry stays pending and independently
      // cancellable -- with its own dispatch/cutoff/cleanup IDs -- until its
      // own dispatch fires, so two same-pitch entries created before either
      // dispatches remain separate registry entries and dispatch in
      // audio-time order rather than call/creation order, giving an
      // earlier-attacking same-pitch entry the chance to dispatch (and
      // retire a later one, or be retired by it) in the correct order
      // (mandatory keyUp-before-keyDown).
      entry.dispatchTimeoutId = scheduleAt(time, () => dispatchEntry(entry));
      if (cutoffTime !== undefined) {
        entry.cutoffTimeoutId = scheduleAt(cutoffTime, () => cutoffEntry(entry));
      }
      entry.cleanupTimeoutId = scheduleAt((cutoffTime ?? time) + 0.001, () => cleanupEntry(entry));
    },
    [isLoaded, retireOwner, scheduleAt, dispatchEntry, cutoffEntry, cleanupEntry]
  );

  // Stop a note, optionally at an explicit audio time: clears its owning
  // entry's cutoff/cleanup timers and entry, then releases it.
  const stopNote = useCallback(
    (pitch: number, time?: number) => {
      if (!pianoRef.current || !isLoaded) return;
      retireOwner(pitch, time ?? getAudioNow());
    },
    [isLoaded, retireOwner]
  );

  // Cancels every strictly-future scheduled attack (`attackTime > audioNow`):
  // clears all three of its timers; an undispatched entry never held a note
  // so needs no keyUp; a dispatched-but-still-future entry (fired early via
  // the lookAhead margin) gets exactly one keyUp at its attackTime. Its
  // `ownerRef` mapping is deleted only if it is still the current owner for
  // that pitch -- a newer same-pitch entry may have already dispatched and
  // taken over ownership, and that mapping must never be erased. The entry
  // itself is always removed from the registry. Never touches an entry whose
  // attackTime has already passed -- those notes keep sounding with their
  // own cutoff intact (tempo-only reconciliation).
  const cancelScheduled = useCallback(() => {
    const instrument = pianoRef.current;
    if (!instrument) return;

    const audioNow = getAudioNow();
    for (const [id, entry] of entriesRef.current) {
      if (entry.attackTime > audioNow) {
        clearEntryTimers(entry);
        if (entry.dispatched && ownerRef.current.get(entry.pitch) === id) {
          instrument.keyUp({ midi: entry.pitch, time: entry.attackTime });
          ownerRef.current.delete(entry.pitch);
        }
        recordSeamCancel(entry.pitch, entry.attackTime);
        entriesRef.current.delete(id);
      }
    }
  }, [clearEntryTimers]);

  // Stop all notes: cancel every pending future attack first (no future
  // attack survives), clear timers on whatever else remains registered,
  // silence the instrument outright, then clear all ownership bookkeeping.
  const stopAllNotes = useCallback(() => {
    const instrument = pianoRef.current;
    if (!instrument) return;

    cancelScheduled();
    for (const entry of entriesRef.current.values()) {
      clearEntryTimers(entry);
    }
    entriesRef.current.clear();
    instrument.stopAll();
    ownerRef.current.clear();
  }, [cancelScheduled, clearEntryTimers]);

  return {
    playNote,
    stopNote,
    stopAllNotes,
    cancelScheduled,
    isLoaded,
    loadState,
    retry,
    loadingProgress,
    startAudio,
    isAudioStarted,
  };
}
